import axios from 'axios';

const BACKEND_URL = 'https://back.agsseguros.online/api/acceso-sharepoint';

// Tipos de archivo soportados por Gemini para análisis
const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
];

const MAX_FILES = 10;
const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB por archivo (límite Gemini inline)
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB total

/**
 * Valida que el link sea una URL de SharePoint válida
 */
export function isValidSharepointUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return parsed.hostname.includes('sharepoint.com');
  } catch {
    return false;
  }
}

/**
 * Un 404 de Express con HTML "Cannot POST /api/..." significa que la ruta no está montada en el
 * backend (deploy sin el módulo de SharePoint), no que la carpeta no exista.
 */
function esRutaInexistente(err) {
  const data = err?.response?.data;
  return err?.response?.status === 404 && typeof data === 'string' && /Cannot (POST|GET)/i.test(data);
}
const ERROR_RUTA = 'El servicio de SharePoint no está disponible en el backend (ruta /api/acceso-sharepoint no encontrada). Avisale a sistemas.';

/**
 * Obtiene los documentos de la carpeta SharePoint del siniestro
 * Llama al backend proxy que se autentica con Entra ID
 * 
 * @param {string} sharepointUrl - URL del link SharePoint del siniestro (ej: https://agsnetsrl.sharepoint.com/:f:/s/SISTEMA-SINIESTROS/...)
 * @returns {Promise<{success: boolean, archivos: Array<{nombre: string, mimeType: string, base64: string, size: number}>, error?: string}>}
 */
export async function fetchSharepointDocuments(sharepointUrl) {
  if (!isValidSharepointUrl(sharepointUrl)) {
    return { success: false, archivos: [], error: 'El link de SharePoint no es válido.' };
  }

  // Normalizar URL
  const normalizedUrl = sharepointUrl.startsWith('http') ? sharepointUrl : `https://${sharepointUrl}`;

  try {
    const response = await axios.post(BACKEND_URL, {
      sharepointUrl: normalizedUrl,
      maxFiles: MAX_FILES,
      maxFileSize: MAX_FILE_SIZE,
      supportedTypes: SUPPORTED_MIME_TYPES,
    }, {
      timeout: 60000, // 60 segundos timeout (descargar archivos puede tardar)
    });

    if (!response.data || !response.data.success) {
      return {
        success: false,
        archivos: [],
        error: response.data?.error || 'No se pudo acceder a la carpeta de SharePoint.',
      };
    }

    // Filtrar archivos que excedan el tamaño máximo y limitar cantidad
    let archivos = (response.data.archivos || [])
      .filter(a => a.size <= MAX_FILE_SIZE)
      .slice(0, MAX_FILES);

    // Verificar tamaño total
    let totalSize = 0;
    const archivosFiltrados = [];
    for (const archivo of archivos) {
      if (totalSize + archivo.size > MAX_TOTAL_SIZE) break;
      totalSize += archivo.size;
      archivosFiltrados.push(archivo);
    }

    return {
      success: true,
      archivos: archivosFiltrados,
      totalArchivos: response.data.totalArchivos || archivosFiltrados.length,
      carpetaNombre: response.data.carpetaNombre || 'Carpeta SharePoint',
    };
  } catch (err) {
    console.error('Error al obtener documentos de SharePoint:', err);

    if (err.code === 'ECONNABORTED') {
      return { success: false, archivos: [], error: 'Tiempo de espera agotado al conectar con SharePoint.' };
    }

    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, archivos: [], error: 'Sin permisos para acceder a esta carpeta de SharePoint.' };
    }

    if (esRutaInexistente(err)) {
      return { success: false, archivos: [], error: ERROR_RUTA };
    }
    if (err.response?.status === 404) {
      return { success: false, archivos: [], error: 'La carpeta de SharePoint no fue encontrada.' };
    }

    return {
      success: false,
      archivos: [],
      error: err.response?.data?.error || err.message || 'Error al conectar con SharePoint.',
    };
  }
}

// Tipos adicionales para la galería "Documentos nube" (solo se muestran/descargan, no van a Gemini)
const GALLERY_MIME_TYPES = [
  ...SUPPORTED_MIME_TYPES,
  'image/bmp',
  'image/heic',
  'image/svg+xml',
  'application/msword', // .doc
  'application/vnd.ms-excel', // .xls
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/zip',
  'application/x-rar-compressed',
  'video/mp4',
  'video/quicktime',
  'audio/mpeg',
  'audio/ogg',
  'application/octet-stream',
];

const GALLERY_MAX_FILES = 60;
const GALLERY_MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB por archivo

/**
 * Obtiene los documentos de la carpeta SharePoint para la galería "Documentos nube".
 * Usa el mismo proxy que el bot pero sin los límites de Gemini.
 *
 * @param {string} sharepointUrl
 * @returns {Promise<{success: boolean, archivos: Array<{nombre: string, mimeType: string, base64: string, size: number}>, carpetaNombre?: string, error?: string}>}
 */
export async function fetchSharepointGallery(sharepointUrl) {
  if (!isValidSharepointUrl(sharepointUrl)) {
    return { success: false, archivos: [], error: 'El link de SharePoint no es válido.' };
  }

  const normalizedUrl = sharepointUrl.startsWith('http') ? sharepointUrl : `https://${sharepointUrl}`;

  try {
    const response = await axios.post(
      BACKEND_URL,
      {
        sharepointUrl: normalizedUrl,
        maxFiles: GALLERY_MAX_FILES,
        maxFileSize: GALLERY_MAX_FILE_SIZE,
        supportedTypes: GALLERY_MIME_TYPES,
      },
      { timeout: 120000 }
    );

    if (!response.data || !response.data.success) {
      return {
        success: false,
        archivos: [],
        error: response.data?.error || 'No se pudo acceder a la carpeta de SharePoint.',
      };
    }

    return {
      success: true,
      archivos: response.data.archivos || [],
      totalArchivos: response.data.totalArchivos || (response.data.archivos || []).length,
      carpetaNombre: response.data.carpetaNombre || 'Carpeta SharePoint',
    };
  } catch (err) {
    console.error('Error al obtener galería de SharePoint:', err);

    if (err.code === 'ECONNABORTED') {
      return { success: false, archivos: [], error: 'Tiempo de espera agotado al conectar con SharePoint.' };
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, archivos: [], error: 'Sin permisos para acceder a esta carpeta de SharePoint.' };
    }
    if (esRutaInexistente(err)) {
      return { success: false, archivos: [], error: ERROR_RUTA };
    }
    if (err.response?.status === 404) {
      return { success: false, archivos: [], error: 'La carpeta de SharePoint no fue encontrada.' };
    }
    return {
      success: false,
      archivos: [],
      error: err.response?.data?.error || err.message || 'Error al conectar con SharePoint.',
    };
  }
}

const UPLOAD_URL = `${BACKEND_URL}/subir`;
export const UPLOAD_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB por archivo (Graph usa upload session por chunks arriba de 4MB)
export const UPLOAD_MAX_FILES = 20;

/**
 * Sube archivos a la carpeta SharePoint del siniestro (multipart/form-data).
 *
 * Contrato backend  POST /api/acceso-sharepoint/subir
 *   body (form-data): sharepointUrl (string) + archivos (File, repetido)
 *   respuesta: { success: boolean, subidos: [{ nombre, size }], errores: [{ nombre, error }], error?: string }
 *
 * @param {string} sharepointUrl
 * @param {File[]} files
 * @param {(porcentaje: number) => void} [onProgress]
 */
export async function uploadSharepointFiles(sharepointUrl, files, onProgress) {
  if (!isValidSharepointUrl(sharepointUrl)) {
    return { success: false, subidos: [], errores: [], error: 'El link de SharePoint no es válido.' };
  }
  if (!files || files.length === 0) {
    return { success: false, subidos: [], errores: [], error: 'No hay archivos para subir.' };
  }

  const normalizedUrl = sharepointUrl.startsWith('http') ? sharepointUrl : `https://${sharepointUrl}`;

  const formData = new FormData();
  formData.append('sharepointUrl', normalizedUrl);
  files.forEach(f => formData.append('archivos', f, f.name));

  try {
    const response = await axios.post(UPLOAD_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min: archivos grandes se suben por chunks
      onUploadProgress: evt => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });

    if (!response.data || !response.data.success) {
      return {
        success: false,
        subidos: response.data?.subidos || [],
        errores: response.data?.errores || [],
        error: response.data?.error || 'No se pudieron subir los archivos a SharePoint.',
      };
    }

    return {
      success: true,
      subidos: response.data.subidos || [],
      errores: response.data.errores || [],
    };
  } catch (err) {
    console.error('Error al subir archivos a SharePoint:', err);

    if (err.code === 'ECONNABORTED') {
      return { success: false, subidos: [], errores: [], error: 'Tiempo de espera agotado al subir a SharePoint.' };
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, subidos: [], errores: [], error: 'Sin permisos para escribir en esta carpeta de SharePoint.' };
    }
    if (esRutaInexistente(err)) {
      return { success: false, subidos: [], errores: [], error: 'El endpoint de subida (/api/acceso-sharepoint/subir) no existe todavía en el backend.' };
    }
    if (err.response?.status === 404) {
      return { success: false, subidos: [], errores: [], error: 'La carpeta de SharePoint no fue encontrada.' };
    }
    if (err.response?.status === 413) {
      return { success: false, subidos: [], errores: [], error: 'Los archivos superan el tamaño máximo permitido por el servidor.' };
    }
    return {
      success: false,
      subidos: [],
      errores: [],
      error: err.response?.data?.error || err.message || 'Error al subir a SharePoint.',
    };
  }
}

const BUSCAR_CARPETA_URL = `${BACKEND_URL}/buscarCarpetaStro`;

/**
 * Busca la carpeta del siniestro en TODAS las bibliotecas del sitio SISTEMA-SINIESTROS,
 * para los casos en que la denuncia todavía no tiene el link cargado (las carpetas nuevas
 * se están creando en otra biblioteca del mismo sitio).
 *
 * Las carpetas se crean con el NRO DE DENUNCIA como nombre; recién cuando cargas genera el
 * siniestro se renombran a "nroStro-poliza". Por eso se busca por los dos números.
 *
 * Contrato backend  POST /api/acceso-sharepoint/buscarCarpetaStro
 *   body (json): { nroStro, entryId, terminos: string[], idSiniestro, vincular, carpetaId, driveId }
 *       terminos -> nro corto de siniestro y/o nro de denuncia, ya normalizados por el front
 *       vincular -> si hay UNA sola carpeta (o vino carpetaId), el backend genera el sharing link
 *                   y lo guarda en la denuncia `idSiniestro`, y lo devuelve en `link`
 *   respuesta:
 *   {
 *     success: true,
 *     carpetas: [{ id, driveId, nombre, biblioteca, webUrl, cantidadArchivos }],  // candidatas
 *     elegida: { id, driveId, nombre, biblioteca, webUrl } | null,               // si hay una sola
 *     archivos: [{ nombre, mimeType, base64, size }],                            // de `elegida`
 *     link: "https://...sharepoint.com/:f:/s/..." | null                         // si vincular=true
 *   }
 *   Sin coincidencias: { success: true, carpetas: [], elegida: null, archivos: [], link: null }
 *
 * @param {{ nroStro?: number|string, entryId?: number|string, id?: number|string }} stro
 * @param {{ carpetaId?: string, driveId?: string, vincular?: boolean }} [opciones]
 */
export async function buscarCarpetaDeStro({ nroStro, entryId, id }, opciones = {}) {
  const { carpetaId, driveId, vincular = true } = opciones;
  const terminos = [];
  const corto = nroStroCorto(nroStro);
  if (corto) terminos.push(corto);
  const entry = String(entryId ?? '').trim();
  if (entry && /^\d+$/.test(entry) && entry !== corto) terminos.push(entry);

  if (!terminos.length) {
    return { success: false, carpetas: [], elegida: null, archivos: [], error: 'El siniestro no tiene número de siniestro ni de denuncia para buscar.' };
  }

  try {
    const response = await axios.post(
      BUSCAR_CARPETA_URL,
      { nroStro, entryId, terminos, idSiniestro: id, vincular: vincular && !!id, carpetaId, driveId },
      { timeout: 120000 }
    );

    if (!response.data || !response.data.success) {
      return { success: false, carpetas: [], elegida: null, archivos: [], error: response.data?.error || 'No se pudo buscar la carpeta en SharePoint.' };
    }

    return {
      success: true,
      carpetas: response.data.carpetas || [],
      elegida: response.data.elegida || null,
      archivos: response.data.archivos || [],
      link: response.data.link || null,
      terminos,
    };
  } catch (err) {
    console.error('Error al buscar la carpeta del siniestro:', err);

    if (esRutaInexistente(err)) {
      return { success: false, carpetas: [], elegida: null, archivos: [], error: 'El endpoint de búsqueda de carpeta (/api/acceso-sharepoint/buscarCarpetaStro) no existe todavía en el backend.' };
    }
    if (err.code === 'ECONNABORTED') {
      return { success: false, carpetas: [], elegida: null, archivos: [], error: 'Tiempo de espera agotado al buscar la carpeta en SharePoint.' };
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, carpetas: [], elegida: null, archivos: [], error: 'Sin permisos para buscar en las bibliotecas del sitio.' };
    }
    return { success: false, carpetas: [], elegida: null, archivos: [], error: err.response?.data?.error || err.message || 'Error al buscar la carpeta.' };
  }
}

const CARPETA_FIJA_URL = `${BACKEND_URL}/buscarEnCarpeta`;

/**
 * Carpetas fijas del sitio SISTEMA-SINIESTROS donde se buscan documentos por nro de siniestro.
 * El front manda solo la `key`; el backend la mapea a la ruta real (whitelist), así no viajan
 * rutas del sitio en el cliente.
 */
export const CARPETAS_FIJAS = [
  { key: 'aMoron', label: 'A MORON', ruta: 'A MORON' },
  { key: 'bajasScan', label: 'BAJAS SCAN', ruta: 'A MORON/BAJAS ESCANEADAS' },
];

/**
 * Nro de siniestro "corto": el que se muestra en pantalla, sin el prefijo 300.
 * 300260483 -> "260483"
 */
export function nroStroCorto(nroStro) {
  if (nroStro === null || nroStro === undefined || nroStro === '') return null;
  const s = String(nroStro).trim();
  if (!/^\d+$/.test(s)) return null;
  return s.length > 6 ? s.substring(s.length - 6) : s;
}

/**
 * Busca en una carpeta fija del sitio SISTEMA-SINIESTROS los archivos cuyo nombre contenga
 * el nro de siniestro corto. El nombre puede incluir otras palabras: se interpreta solo el número.
 *
 * Contrato backend  POST /api/acceso-sharepoint/buscarEnCarpeta
 *   body (json): { carpeta: 'aMoron' | 'bajasScan', termino: string, nroStro: number|string }
 *       carpeta -> el backend la mapea: aMoron = "A MORON", bajasScan = "A MORON/BAJAS ESCANEADAS"
 *       termino -> nro corto, ej "260483"
 *   respuesta: { success: boolean, archivos: [{ nombre, mimeType, base64, size }], error?: string }
 *   Si no hay coincidencias: { success: true, archivos: [] }
 *
 * @param {string} carpetaKey  key de CARPETAS_FIJAS
 * @param {number|string} nroStro
 */
export async function fetchCarpetaFijaDocs(carpetaKey, nroStro) {
  const carpeta = CARPETAS_FIJAS.find(c => c.key === carpetaKey);
  if (!carpeta) {
    return { success: false, archivos: [], error: 'Carpeta no configurada.' };
  }

  const termino = nroStroCorto(nroStro);
  if (!termino) {
    return { success: false, archivos: [], error: 'El siniestro no tiene número asignado todavía.' };
  }

  try {
    const response = await axios.post(CARPETA_FIJA_URL, { carpeta: carpeta.key, termino, nroStro }, { timeout: 120000 });

    if (!response.data || !response.data.success) {
      return { success: false, archivos: [], error: response.data?.error || `No se pudo consultar la carpeta ${carpeta.label}.` };
    }

    return { success: true, archivos: response.data.archivos || [], termino };
  } catch (err) {
    console.error(`Error al buscar documentos en ${carpeta.label}:`, err);

    if (esRutaInexistente(err)) {
      return { success: false, archivos: [], error: 'El endpoint de búsqueda (/api/acceso-sharepoint/buscarEnCarpeta) no existe todavía en el backend.' };
    }
    if (err.code === 'ECONNABORTED') {
      return { success: false, archivos: [], error: `Tiempo de espera agotado al consultar ${carpeta.label}.` };
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, archivos: [], error: `Sin permisos para acceder a la carpeta ${carpeta.label}.` };
    }
    if (err.response?.status === 404) {
      return { success: false, archivos: [], error: `No se encontró la carpeta ${carpeta.label}.` };
    }
    return { success: false, archivos: [], error: err.response?.data?.error || err.message || `Error al consultar ${carpeta.label}.` };
  }
}

/**
 * Nombre que debe tener la carpeta una vez cargado el siniestro: "nroStroCorto-poliza".
 * Antes de la carga la carpeta se llama con el nro de denuncia.
 * Ej: nroStro 300261246 + pza 23829373 -> "261246-23829373"
 *
 * @returns {string|null} null si falta alguno de los dos datos
 */
export function nombreCarpetaStro(nroStro, pza) {
  const corto = nroStroCorto(nroStro);
  const poliza = String(pza ?? '').trim();
  if (!corto || !poliza || poliza === '0') return null;
  return `${corto}-${poliza}`;
}

const RENAME_URL = `${BACKEND_URL}/renombrar`;

// SharePoint/OneDrive rechaza estos caracteres en nombres de carpeta
const CARACTERES_INVALIDOS = /[\\/:*?"<>|#%]/;

/**
 * Valida un nombre de carpeta antes de mandarlo al backend.
 * @returns {string|null} mensaje de error, o null si es válido
 */
export function validarNombreCarpeta(nombre) {
  const n = (nombre || '').trim();
  if (!n) return 'El nombre no puede estar vacío.';
  if (n.length > 255) return 'El nombre es demasiado largo (máx. 255 caracteres).';
  if (CARACTERES_INVALIDOS.test(n)) return 'El nombre no puede contener \\ / : * ? " < > | # %';
  if (n.startsWith('.') || n.endsWith('.')) return 'El nombre no puede empezar ni terminar con punto.';
  return null;
}

/**
 * Renombra la carpeta SharePoint del siniestro.
 *
 * Contrato backend  POST /api/acceso-sharepoint/renombrar
 *   body (json): { sharepointUrl: string, nuevoNombre: string }
 *   respuesta: { success: boolean, carpetaNombre?: string, error?: string }
 *
 * El link compartido apunta al ID del item, así que sigue siendo válido después de renombrar.
 *
 * @param {string} sharepointUrl
 * @param {string} nuevoNombre
 */
export async function renameSharepointFolder(sharepointUrl, nuevoNombre) {
  if (!isValidSharepointUrl(sharepointUrl)) {
    return { success: false, error: 'El link de SharePoint no es válido.' };
  }
  const errorNombre = validarNombreCarpeta(nuevoNombre);
  if (errorNombre) {
    return { success: false, error: errorNombre };
  }

  const normalizedUrl = sharepointUrl.startsWith('http') ? sharepointUrl : `https://${sharepointUrl}`;

  try {
    const response = await axios.post(
      RENAME_URL,
      { sharepointUrl: normalizedUrl, nuevoNombre: nuevoNombre.trim() },
      { timeout: 30000 }
    );

    if (!response.data || !response.data.success) {
      return { success: false, error: response.data?.error || 'No se pudo renombrar la carpeta.' };
    }

    return { success: true, carpetaNombre: response.data.carpetaNombre || nuevoNombre.trim() };
  } catch (err) {
    console.error('Error al renombrar la carpeta de SharePoint:', err);

    if (esRutaInexistente(err)) {
      return { success: false, error: 'El endpoint para renombrar (/api/acceso-sharepoint/renombrar) no existe todavía en el backend.' };
    }
    if (err.response?.status === 401 || err.response?.status === 403) {
      return { success: false, error: 'Sin permisos para renombrar esta carpeta de SharePoint.' };
    }
    if (err.response?.status === 404) {
      return { success: false, error: 'La carpeta de SharePoint no fue encontrada.' };
    }
    if (err.response?.status === 409) {
      return { success: false, error: 'Ya existe otra carpeta con ese nombre.' };
    }
    return { success: false, error: err.response?.data?.error || err.message || 'Error al renombrar la carpeta.' };
  }
}

/**
 * Prepara los documentos SP para enviar como contexto a Gemini
 * Retorna un texto descriptivo + las parts con inlineData
 */
export function prepareDocumentsForGemini(archivos) {
  if (!archivos || archivos.length === 0) return { contextText: '', parts: [] };

  let contextText = `\n=== DOCUMENTOS DE SHAREPOINT (${archivos.length} archivo${archivos.length > 1 ? 's' : ''}) ===\n`;
  const parts = [];

  archivos.forEach((archivo, idx) => {
    contextText += `- Archivo ${idx + 1}: "${archivo.nombre}" (${archivo.mimeType}, ${formatFileSize(archivo.size)})\n`;

    // Solo enviar como inlineData los tipos que Gemini soporta directamente
    if (archivo.base64 && isGeminiInlineSupported(archivo.mimeType)) {
      parts.push({
        inlineData: {
          mimeType: archivo.mimeType,
          data: archivo.base64,
        },
      });
    }
  });

  return { contextText, parts };
}

function isGeminiInlineSupported(mimeType) {
  const supported = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  return supported.includes(mimeType);
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export { formatFileSize };
